import { useEffect, useState, type ReactNode } from "react";
import { AlertModal, iconOptions } from "@andorhealth/clientuireact.designsystem";
import { useConfiguration } from "../hooks/useConfiguration";
import {
  UnsavedChangesContext,
  type LeaveRequest,
} from "../hooks/useUnsavedChanges";
import { STEPS, saveGroupForStep, type SaveGroup } from "../lib/steps";

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const {
    mode,
    stepIndex,
    setStepIndex,
    setMode,
    policyDirty,
    requestsDirty,
    defaultsDirty,
    discardPolicyEdits,
    discardRequestEdits,
    discardDefaultEdits,
  } = useConfiguration();
  const [pending, setPending] = useState<LeaveRequest | null>(null);

  const dirtyGroups = (): SaveGroup[] => {
    const groups: SaveGroup[] = [];
    if (policyDirty) groups.push("policy");
    if (requestsDirty) groups.push("requests");
    if (defaultsDirty) groups.push("defaults");
    return groups;
  };

  const discard = (groups: SaveGroup[]) => {
    if (groups.includes("policy")) discardPolicyEdits();
    if (groups.includes("requests")) discardRequestEdits();
    if (groups.includes("defaults")) discardDefaultEdits();
  };

  const requestStep = (index: number) => {
    const from =
      mode === "wizard" ? saveGroupForStep(STEPS[stepIndex].id) : null;
    const to = saveGroupForStep(STEPS[index].id);
    if (from && from !== to && dirtyGroups().includes(from)) {
      setPending({
        groups: [from],
        discard: false,
        action: () => setStepIndex(index),
      });
      return;
    }
    setStepIndex(index);
  };

  const requestHub = () => {
    const groups = dirtyGroups();
    const go = () => {
      setMode("hub");
      setStepIndex(0);
    };
    if (groups.length === 0) {
      go();
      return;
    }
    setPending({ groups, discard: true, action: go });
  };

  useEffect(() => {
    const onLeave = (event: BeforeUnloadEvent) => {
      if (!policyDirty && !requestsDirty && !defaultsDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [policyDirty, requestsDirty, defaultsDirty]);

  return (
    <UnsavedChangesContext.Provider value={{ requestStep, requestHub }}>
      {children}
      <AlertModal
        open={pending !== null}
        icon={iconOptions.WARNING}
        title="Leave without saving?"
        subtitle="These edits on this step are not saved yet."
        onClose={() => setPending(null)}
        buttons={[
          {
            label: "Stay",
            variant: "secondary",
            onClick: () => setPending(null),
          },
          {
            label: "Leave",
            variant: "primary",
            onClick: () => {
              if (!pending) return;
              if (pending.discard) discard(pending.groups);
              pending.action();
              setPending(null);
            },
          },
        ]}
      />
    </UnsavedChangesContext.Provider>
  );
}
